import { AppRoute, Breadcrumb, SerializedPage } from '@abovo/shared-models';
import { RouterHelper, getResolvedUrl } from '@abovo/shared-utils';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import * as fromRouter from '@ngrx/router-store';

/**
 * The custom routerstate serializer
 * use this to append stuff to the routerstate
 */
@Injectable()
export class CustomRouterSerializer implements fromRouter.RouterStateSerializer<SerializedPage> {
   serialize(routerState: RouterStateSnapshot): SerializedPage {
      const { url } = routerState;
      const { queryParams } = routerState.root;

      let state: ActivatedRouteSnapshot = routerState.root;
      let params = state.params;
      let data = state.data;

      while (state.firstChild) {
         state = state.firstChild;

         if (Object.keys(state.params).length) {
            // merge all params to main object, to keep all params of all activated routes including the parents
            params = Object.assign({}, params, state.params);
         }

         data = state.data;
      }

      // The page history entry is the nearest ancestor marked as a page: sub-routes of a page (its
      // tabs and sub-navigation) share the entry of the page itself. Read the route's own config
      // instead of the snapshot data, which inherits `pageHistoryEntry` from a parent route and
      // would make a deeper route the entry.
      let historyItem: ActivatedRouteSnapshot = state;
      while (!historyItem?.routeConfig?.data?.['pageHistoryEntry']) {
         if (!historyItem?.parent) break;
         historyItem = historyItem.parent;
      }
      const historyItemUrl = historyItem.pathFromRoot
         .map((av) => av.url.map((segment) => segment.toString()).join('/'))
         .filter(Boolean)
         .join('/');

      return {
         url,
         slug: RouterHelper.slugifyRouteUrl(url),
         slugWithoutQueryParams: RouterHelper.slugifyRouteUrl(url).split('?')[0],
         queryParams,
         params,
         data,
         title: this.findTitle(state),
         breadcrumbs: this.buildBreadcrumbs(state),
         historyItem: {
            url: historyItemUrl,
            slug: [
               RouterHelper.slugifyRouteUrl(historyItemUrl),
               Object.entries(historyItem.queryParams)
                  .map(([key, value]) => `${key}=${value}`)
                  .join('&'),
            ]
               .filter(Boolean)
               .join('?'),
            slugWithoutQueryParams: RouterHelper.slugifyRouteUrl(historyItemUrl).split('?')[0],
            queryParams: historyItem.queryParams,
            params: historyItem.params,
            data: historyItem.data,
            // The title of the page being viewed, not of the history entry route: a detail page
            // resolves its title (client, campaign) on a route below the one marked as page, so
            // looking it up from there would only find the static title of an ancestor.
            title: this.findTitle(state),
         },
      };
   }

   /**
    * The breadcrumb trail of the current page: one crumb per named ancestor route, named by its
    * `breadcrumb` or, failing that, its own title. The current route is only named by its own
    * `breadcrumb` — without one it gets no crumb, since the page title already names it. That
    * last crumb is the current place, so it renders inactive instead of as a link.
    */
   private buildBreadcrumbs(state: ActivatedRouteSnapshot): Breadcrumb[] {
      const routesInTrail = state.pathFromRoot.filter((route) => route.url.length > 0 && !!route.routeConfig);
      const currentRoute = routesInTrail.at(-1);

      return routesInTrail
         .map((route) => {
            // Use the route's own config, not the snapshot data: a route below a component-less
            // parent inherits that parent's data, which would repeat the parent's crumb here.
            const routeData: AppRoute['data'] = route.routeConfig?.data;
            const ownTitle = typeof route.routeConfig?.title === 'string' ? route.routeConfig.title : null;
            const isCurrentRoute = route === currentRoute;
            const name = isCurrentRoute ? routeData?.breadcrumb : (routeData?.breadcrumb ?? ownTitle);
            return name ? { path: `/${getResolvedUrl(route)}`, name, isCurrentPage: isCurrentRoute } : null;
         })
         .filter((crumb) => !!crumb);
   }

   private findTitle(state: ActivatedRouteSnapshot) {
      if (state.title) {
         return state.title;
      } else if (state.parent) {
         let routeWithTitle = state;
         while (!routeWithTitle.title) {
            if (!routeWithTitle.parent) {
               break;
            }
            routeWithTitle = routeWithTitle.parent;
         }
         return routeWithTitle.title ?? '';
      }
      return '';
   }
}
